import { useQuery } from "@tanstack/react-query";
import axiosInstance from "../../../../services/api";
import { API_URL } from "@/constants";
import type {
  DocumentSummaryStats,
  SessionStatistics,
  VideoSummaryStats,
} from "types/";
import { ANALYTICS_STALE_TIME } from "../../components/analytics/constants/constants";
import type { AnalyticsDateRange } from "./useAnalyticsDateRange";

export type ProjectKpis = {
  totalViews: number | null;
  avgReadDepthPct: number | null;
  medianDropSeconds: number | null;
  completionRatePct: number | null;
};

type KpiState = { value: number | null; isLoading: boolean };

const projectBase = (projectId: string | undefined) =>
  `${API_URL}/analytics/projects/${projectId}`;

/**
 * The four headline figures of a project's analytics pages, for the date
 * range picked in the URL. The video figures follow the picked version, like
 * the video cards below them; the others count every version. A failed
 * request reads as null.
 */
export function useProjectKpis(
  projectId: string | undefined,
  dateRange: AnalyticsDateRange,
  version?: string
): Record<keyof ProjectKpis, KpiState> {
  const from = dateRange.startDate?.format("YYYY-MM-DD");
  const to = dateRange.endDate?.format("YYYY-MM-DD");
  const enabled = !!projectId && !!from && !!to;
  const params = { from_date: from, to_date: to };

  const get = <T>(path: string, extraParams?: Record<string, string>) => ({
    queryFn: async ({ signal }: { signal?: AbortSignal }) => {
      const { data } = await axiosInstance.get<T>(
        `${projectBase(projectId)}/${path}`,
        { params: { ...params, ...extraParams }, signal }
      );
      return data;
    },
    enabled,
    staleTime: ANALYTICS_STALE_TIME,
  });

  const sessions = useQuery({
    queryKey: ["analytics", projectId, "kpis", "sessions", from, to],
    ...get<SessionStatistics[]>("sessions/v2"),
  });
  const document = useQuery({
    queryKey: ["analytics", projectId, "kpis", "document", from, to],
    ...get<DocumentSummaryStats>("analytics/document/summary"),
  });
  const video = useQuery({
    queryKey: ["analytics", projectId, "kpis", "video", from, to, version],
    ...get<VideoSummaryStats>(
      "analytics/video/summary",
      version ? { project_publication_id: version } : undefined
    ),
  });

  const totalViews = sessions.data
    ? sessions.data.reduce((sum, point) => sum + point.sessions, 0)
    : null;

  return {
    totalViews: { value: totalViews, isLoading: sessions.isInitialLoading },
    avgReadDepthPct: {
      value: document.data?.avg_reading_depth_pct ?? null,
      isLoading: document.isInitialLoading,
    },
    medianDropSeconds: {
      value: video.data?.median_drop_seconds ?? null,
      isLoading: video.isInitialLoading,
    },
    completionRatePct: {
      value: video.data?.full_completers_pct ?? null,
      isLoading: video.isInitialLoading,
    },
  };
}
